import { Component, input, output, signal, forwardRef, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './app-input.component.html',
  styleUrl: './app-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppInputComponent),
      multi: true
    }
  ]
})
export class AppInputComponent implements ControlValueAccessor {
  // Input signals - modern Angular approach
  type = input<'text' | 'email' | 'password' | 'url'>('text');
  placeholder = input<string>('');
  label = input<string>('');
  required = input<boolean>(false);
  disabledInput = input<boolean>(false);
  error = input<string>('');
  size = input<'sm' | 'base' | 'lg'>('base');
  variant = input<'default' | 'outline' | 'filled'>('default');

  // Local signal for disabled state (can be controlled by ControlValueAccessor)
  private _disabledState = signal(false);

  inputId = `app-input-${Math.random().toString(36).substr(2, 9)}`;

  // Output signals - modern Angular approach
  valueChange = output<string>();
  blur = output<Event>();
  focus = output<Event>();

  private _value = signal('');
  private _onChange = (value: string) => {};
  private _onTouched = () => {};

  // Computed signal for reactive value
  valueSignal = computed(() => this._value());

  // Computed signal for disabled state (combines input and ControlValueAccessor state)
  disabled = computed(() => this.disabledInput() || this._disabledState());

  // Computed signals for CSS classes - now using input signals directly
  inputClasses = computed(() => {
    const classes = [
      'w-full',
      'transition-colors',
      'duration-200',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-brand-orange',
      'focus:ring-opacity-50'
    ];

    // Size classes
    switch (this.size()) {
      case 'sm':
        classes.push('px-3', 'py-2', 'text-sm');
        break;
      case 'lg':
        classes.push('px-4', 'py-3', 'text-lg');
        break;
      default:
        classes.push('px-3', 'py-2', 'text-base');
    }

    // Variant classes
    switch (this.variant()) {
      case 'outline':
        classes.push('border', 'border-gray-300', 'bg-transparent', 'focus:border-brand-orange');
        break;
      case 'filled':
        classes.push('border', 'border-transparent', 'bg-gray-100', 'focus:bg-white', 'focus:border-brand-orange');
        break;
      default:
        classes.push('border', 'border-gray-300', 'bg-white', 'focus:border-brand-orange');
    }

    // Error state
    if (this.error()) {
      classes.push('border-red-500', 'focus:border-red-500', 'focus:ring-red-500');
    }

    // Disabled state
    if (this.disabled()) {
      classes.push('opacity-50', 'cursor-not-allowed');
    }

    return classes.join(' ');
  });

  labelClasses = computed(() => {
    const classes = ['block', 'font-medium'];
    
    switch (this.size()) {
      case 'sm':
        classes.push('text-sm', 'mb-1');
        break;
      case 'lg':
        classes.push('text-lg', 'mb-2');
        break;
      default:
        classes.push('text-base', 'mb-1');
    }

    if (this.error()) {
      classes.push('text-red-600');
    } else {
      classes.push('text-gray-700');
    }

    return classes.join(' ');
  });

  set value(val: string) {
    this._value.set(val);
    this._onChange(val);
    this.valueChange.emit(val);
  }

  onInput(event: Event) {
    const target = event.target as HTMLInputElement;
    this.value = target.value;
  }

  onBlur(event: Event) {
    this._onTouched();
    this.blur.emit(event);
  }

  onFocus(event: Event) {
    this.focus.emit(event);
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this._value.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledState.set(isDisabled);
  }
}